/**
 * Dynamically load Leaflet from the CDN and resolve with the global `L`.
 *
 * Leaflet is intentionally not an npm dependency — it is loaded on demand so it
 * only ships to pages that actually render a map. This is the single shared
 * loader for every map (flights map, project location map); the CDN version and
 * SRI hashes live here only.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Leaflet = any;

const LEAFLET_VERSION = '1.9.4';
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS_INTEGRITY =
  'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
const LEAFLET_CSS_INTEGRITY =
  'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';

let leafletPromise: Promise<Leaflet> | null = null;

function getGlobalLeaflet(): Leaflet | undefined {
  return (window as unknown as { L?: Leaflet }).L;
}

export function loadLeaflet(): Promise<Leaflet> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Leaflet requires a browser environment'));
  }

  const existing = getGlobalLeaflet();
  if (existing) return Promise.resolve(existing);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise<Leaflet>((resolve, reject) => {
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.integrity = LEAFLET_CSS_INTEGRITY;
      link.crossOrigin = '';
      document.head.appendChild(link);
    }

    const finish = () => {
      const L = getGlobalLeaflet();
      if (L) {
        resolve(L);
      } else {
        reject(new Error('Leaflet loaded but global `L` is unavailable'));
      }
    };

    const existingScript = document.querySelector('script[src*="leaflet.js"]');
    if (existingScript) {
      // Another loader already started; poll briefly for `L` to appear.
      const interval = setInterval(() => {
        if (getGlobalLeaflet()) {
          clearInterval(interval);
          finish();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(interval);
        if (!getGlobalLeaflet()) {
          reject(new Error('Timed out waiting for Leaflet to load'));
        }
      }, 5000);
      return;
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.integrity = LEAFLET_JS_INTEGRITY;
    script.crossOrigin = '';
    script.onload = finish;
    script.onerror = () => {
      // Remove the dead element so a later retry re-enters this branch and
      // appends a fresh script, instead of getting stuck polling the failed one.
      script.remove();
      reject(new Error('Failed to load Leaflet'));
    };
    document.body.appendChild(script);
  }).catch((err) => {
    // Don't cache a rejected promise — allow a later retry.
    leafletPromise = null;
    throw err;
  });

  return leafletPromise;
}
